<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  pendingFaq: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'approve', 'reject', 'delete']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const showDrawer = ref(false);
const knowledgeBaseFaq = ref(null);
const isLoadingFaq = ref(false);

// 获取状态标签样式
const getStatusConfig = status => {
  const configMap = {
    pending: {
      bgColor: 'bg-n-amber-1 dark:bg-n-amber-2',
      textColor: 'text-n-amber-10 dark:text-n-amber-9',
      dotColor: 'bg-n-amber-5',
    },
    approved: {
      bgColor: 'bg-n-teal-1 dark:bg-n-teal-2',
      textColor: 'text-n-teal-10 dark:text-n-teal-9',
      dotColor: 'bg-n-teal-5',
    },
    rejected: {
      bgColor: 'bg-n-ruby-1 dark:bg-n-ruby-2',
      textColor: 'text-n-ruby-10 dark:text-n-ruby-9',
      dotColor: 'bg-n-ruby-5',
    },
  };

  return (
    configMap[status] || {
      bgColor: 'bg-n-slate-1 dark:bg-n-slate-2',
      textColor: 'text-n-slate-10 dark:text-n-slate-9',
      dotColor: 'bg-n-slate-5',
    }
  );
};

const getStatusText = status => {
  return t(`PILOT.PENDING_FAQS.STATUS.${status.toUpperCase()}`);
};

// 格式化审核人信息
const reviewerInfo = computed(() => {
  if (!props.pendingFaq) return '';
  const parts = [];
  if (props.pendingFaq.reviewer?.name) {
    parts.push(props.pendingFaq.reviewer.name);
  }
  if (props.pendingFaq.reviewed_at) {
    parts.push(dynamicTime(props.pendingFaq.reviewed_at));
  }
  return parts.join(' · ');
});

// 加载知识库 FAQ 详情
const loadKnowledgeBaseFaq = async faqId => {
  if (!faqId) return;
  isLoadingFaq.value = true;
  knowledgeBaseFaq.value = null;
  try {
    const response = await store.dispatch('pilotFaqs/show', { id: faqId });
    knowledgeBaseFaq.value = response.payload || response;
  } catch {
    knowledgeBaseFaq.value = null;
  } finally {
    isLoadingFaq.value = false;
  }
};

// 打开对话
const openConversation = () => {
  if (!props.pendingFaq?.source_conversation) return;
  const { id, inbox_id } = props.pendingFaq.source_conversation;
  const accountId = route.params.accountId || '1';
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId,
      inbox_id: inbox_id,
      conversation_id: id,
    },
  });
  closeDrawer();
};

// 跳转编辑知识库 FAQ
const editKnowledgeBaseFaq = () => {
  if (!knowledgeBaseFaq.value) return;
  const accountId = route.params.accountId || '1';
  router.push({
    name: 'pilot_faqs_edit',
    params: {
      accountId,
      knowledgeBaseId: knowledgeBaseFaq.value.knowledge_base_id,
      faqId: knowledgeBaseFaq.value.id,
    },
  });
  closeDrawer();
};

const openDrawer = () => {
  showDrawer.value = true;
};

const closeDrawer = () => {
  showDrawer.value = false;
  knowledgeBaseFaq.value = null;
  emit('close');
};

const handleApprove = () => {
  emit('approve', props.pendingFaq);
  closeDrawer();
};

const handleReject = () => {
  emit('reject', props.pendingFaq);
  closeDrawer();
};

const handleDelete = () => {
  emit('delete', props.pendingFaq);
  closeDrawer();
};

// 暴露方法供父组件调用
defineExpose({
  open: openDrawer,
  close: closeDrawer,
});

// 当 pendingFaq 变化时打开抽屉并加载数据
watch(
  () => props.pendingFaq,
  newVal => {
    if (newVal) {
      openDrawer();
      if (newVal.status === 'approved' && newVal.faq_id) {
        loadKnowledgeBaseFaq(newVal.faq_id);
      } else {
        knowledgeBaseFaq.value = null;
      }
    }
  }
);
</script>

<template>
  <Teleport to="body">
    <!-- 遮罩层 -->
    <transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showDrawer && pendingFaq"
        class="fixed inset-0 bg-black/50 z-[9998]"
        @click="closeDrawer"
      />
    </transition>

    <!-- 抽屉面板 -->
    <transition
      enter-active-class="transition-transform duration-300 ease-out"
      enter-from-class="-translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transition-transform duration-200 ease-in"
      leave-from-class="translate-x-0"
      leave-to-class="-translate-x-full"
    >
      <div
        v-if="showDrawer && pendingFaq"
        class="fixed left-0 top-0 h-full w-[50rem] bg-n-solid-1 dark:bg-n-slate-900 shadow-2xl z-[9999] flex flex-col"
      >
        <!-- 头部 -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-slate-6 dark:border-n-slate-700"
        >
          <h3 class="text-lg font-medium text-n-slate-12">
            {{ $t('PILOT.PENDING_FAQS.DRAWER.TITLE') }}
          </h3>
          <button
            class="p-1 rounded-lg hover:bg-n-slate-2 dark:hover:bg-n-slate-800 transition-colors"
            @click="closeDrawer"
          >
            <Icon icon="i-lucide-x" class="size-5 text-n-slate-11" />
          </button>
        </div>

        <!-- 内容区域 -->
        <div class="flex-1 overflow-y-auto px-6 py-6">
          <div class="flex flex-col gap-6">
            <!-- ============ 区域一：审核结果 ============ -->
            <div class="p-4 bg-n-slate-2 dark:bg-n-slate-800 rounded-2xl">
              <h4
                class="text-sm font-medium text-n-slate-12 mb-4"
              >
                {{ $t('PILOT.PENDING_FAQS.DRAWER.REVIEW_RESULT') }}
              </h4>

              <!-- 待审核 -->
              <template v-if="pendingFaq.status === 'pending'">
                <div
                  class="flex flex-col items-center justify-center gap-3 py-8 text-center"
                >
                  <Icon
                    icon="i-lucide-clock"
                    class="size-12 text-n-slate-8 mb-2"
                  />
                  <h4 class="text-xl font-medium text-n-slate-12">
                    {{ getStatusText('pending') }}
                  </h4>
                  <p class="max-w-sm text-sm text-n-slate-11">
                    {{ $t('PILOT.PENDING_FAQS.DRAWER.PENDING_HINT') }}
                  </p>
                </div>
              </template>

              <!-- 已通过 -->
              <template v-if="pendingFaq.status === 'approved'">
                <div class="flex items-center justify-between mb-4">
                  <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    :class="[
                      getStatusConfig('approved').bgColor,
                      getStatusConfig('approved').textColor,
                    ]"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      :class="[getStatusConfig('approved').dotColor]"
                    />
                    {{ getStatusText('approved') }}
                  </span>
                  <div class="flex items-center gap-3">
                    <span
                      v-if="reviewerInfo"
                      class="text-sm text-n-slate-11"
                    >
                      {{ reviewerInfo }}
                    </span>
                    <button
                      v-if="knowledgeBaseFaq"
                      class="flex items-center gap-1.5 text-sm text-n-brand hover:underline cursor-pointer"
                      @click="editKnowledgeBaseFaq"
                    >
                      <Icon
                        icon="i-lucide-external-link"
                        class="size-3.5 flex-shrink-0"
                      />
                      {{ $t('PILOT.PENDING_FAQS.DRAWER.EDIT_FAQ') }}
                    </button>
                  </div>
                </div>

                <!-- 加载中 -->
                <div
                  v-if="isLoadingFaq"
                  class="flex items-center justify-center py-4"
                >
                  <Spinner class="size-5" />
                </div>

                <!-- FAQ 已删除 -->
                <template v-else-if="!knowledgeBaseFaq && !isLoadingFaq">
                  <div
                    v-if="pendingFaq.target_knowledge_base"
                    class="mb-5"
                  >
                    <label
                      class="block text-n-slate-12 dark:text-n-slate-12 text-sm font-medium mb-2"
                    >
                      {{ $t('PILOT.PENDING_FAQS.DRAWER.TARGET_KB') }}
                    </label>
                    <div class="p-3 border border-n-weak rounded-lg">
                      <span class="text-sm text-n-slate-11">
                        {{ pendingFaq.target_knowledge_base.name }}
                      </span>
                    </div>
                  </div>
                  <p class="text-sm text-n-slate-11">
                    {{ $t('PILOT.PENDING_FAQS.DRAWER.FAQ_DELETED') }}
                  </p>
                </template>

                <!-- FAQ 详情 -->
                <template v-else>
                  <!-- 目标知识库 -->
                  <div
                    v-if="pendingFaq.target_knowledge_base"
                    class="mb-5"
                  >
                    <label
                      class="block text-n-slate-12 dark:text-n-slate-12 text-sm font-medium mb-2"
                    >
                      {{ $t('PILOT.PENDING_FAQS.DRAWER.TARGET_KB') }}
                    </label>
                    <div class="p-3 border border-n-weak rounded-lg">
                      <span class="text-sm text-n-slate-11">
                        {{ pendingFaq.target_knowledge_base.name }}
                      </span>
                    </div>
                  </div>

                  <!-- 标准问题 -->
                  <div class="mb-5">
                    <label
                      class="block text-n-slate-12 dark:text-n-slate-12 text-sm font-medium mb-2"
                    >
                      {{ $t('PILOT.PENDING_FAQS.DRAWER.FAQ_QUESTION') }}
                    </label>
                    <div class="p-3 border border-n-weak rounded-lg">
                      <p class="text-sm text-n-slate-11">
                        {{ knowledgeBaseFaq.question }}
                      </p>
                    </div>
                  </div>

                  <!-- 相似问题 -->
                  <div
                    v-if="
                      knowledgeBaseFaq.similar_questions &&
                      knowledgeBaseFaq.similar_questions.length
                    "
                    class="mb-5"
                  >
                    <label
                      class="block text-n-slate-12 dark:text-n-slate-12 text-sm font-medium mb-2"
                    >
                      {{ $t('PILOT.PENDING_FAQS.DRAWER.FAQ_SIMILAR_QUESTIONS') }}
                    </label>
                    <ul class="space-y-2">
                      <li
                        v-for="sq in knowledgeBaseFaq.similar_questions"
                        :key="sq.id"
                        class="flex items-center gap-3 p-4 bg-n-slate-white dark:bg-n-slate-800 border border-n-weak rounded-lg"
                      >
                        <div
                          class="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-n-brand text-white"
                        >
                          <Icon
                            icon="i-lucide-help-circle"
                            class="h-5 w-5"
                          />
                        </div>
                        <span
                          class="text-sm text-n-slate-700 dark:text-n-slate-300"
                        >
                          {{ sq.question }}
                        </span>
                      </li>
                    </ul>
                  </div>

                  <!-- 回答 -->
                  <div
                    v-if="
                      knowledgeBaseFaq.answers &&
                      knowledgeBaseFaq.answers.length
                    "
                  >
                    <label
                      class="block text-n-slate-12 dark:text-n-slate-12 text-sm font-medium mb-2"
                    >
                      {{ $t('PILOT.PENDING_FAQS.DRAWER.FAQ_ANSWERS') }}
                    </label>
                    <div class="space-y-4">
                      <div
                        v-for="ans in knowledgeBaseFaq.answers"
                        :key="ans.id"
                        class="border border-n-weak rounded-lg p-3"
                      >
                        <p
                          class="text-sm text-n-slate-11 whitespace-pre-wrap"
                        >
                          {{ ans.answer }}
                        </p>
                      </div>
                    </div>
                  </div>
                </template>
              </template>

              <!-- 已拒绝 -->
              <template v-if="pendingFaq.status === 'rejected'">
                <div class="flex items-center justify-between mb-4">
                  <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    :class="[
                      getStatusConfig('rejected').bgColor,
                      getStatusConfig('rejected').textColor,
                    ]"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      :class="[getStatusConfig('rejected').dotColor]"
                    />
                    {{ getStatusText('rejected') }}
                  </span>
                  <span
                    v-if="reviewerInfo"
                    class="text-sm text-n-slate-11"
                  >
                    {{ reviewerInfo }}
                  </span>
                </div>
                <div class="mb-5">
                  <label
                    class="block text-n-slate-12 dark:text-n-slate-12 text-sm font-medium mb-2"
                  >
                    {{ $t('PILOT.PENDING_FAQS.DRAWER.REJECT_REASON') }}
                  </label>
                  <p class="text-sm text-n-slate-11 whitespace-pre-wrap">
                    {{
                      pendingFaq.review_note ||
                      $t('PILOT.PENDING_FAQS.DRAWER.NO_REJECT_REASON')
                    }}
                  </p>
                </div>
              </template>
            </div>

            <!-- ============ 区域二：AI FAQ 原内容 ============ -->
            <div class="p-4 bg-n-slate-2 dark:bg-n-slate-800 rounded-2xl">
              <h4
                class="text-sm font-medium text-n-slate-12 mb-4"
              >
                {{ $t('PILOT.PENDING_FAQS.DRAWER.AI_ORIGINAL') }}
              </h4>

              <div class="flex flex-col gap-6">
                <!-- 问题 -->
                <div>
                  <label
                    class="block text-n-slate-12 dark:text-n-slate-12 text-sm font-medium mb-2"
                  >
                    {{ $t('PILOT.PENDING_FAQS.DRAWER.QUESTION') }}
                  </label>
                  <p class="text-sm text-n-slate-11">
                    {{ pendingFaq.question }}
                  </p>
                </div>

                <!-- 答案 -->
                <div>
                  <label
                    class="block text-n-slate-12 dark:text-n-slate-12 text-sm font-medium mb-2"
                  >
                    {{ $t('PILOT.PENDING_FAQS.DRAWER.ANSWER') }}
                  </label>
                  <p class="text-sm text-n-slate-11 whitespace-pre-wrap">
                    {{ pendingFaq.answer }}
                  </p>
                </div>

                <!-- 来源对话 -->
                <div v-if="pendingFaq.source_conversation">
                  <label
                    class="block text-n-slate-12 dark:text-n-slate-12 text-sm font-medium mb-2"
                  >
                    {{ $t('PILOT.PENDING_FAQS.DRAWER.SOURCE') }}
                  </label>
                  <button
                    class="flex items-center gap-2 text-sm text-n-brand hover:underline cursor-pointer"
                    @click="openConversation"
                  >
                    <Icon
                      icon="i-lucide-message-square"
                      class="size-4 flex-shrink-0"
                    />
                    <span>
                      {{ $t('PILOT.PENDING_FAQS.DRAWER.SOURCE_CONVERSATION') }}
                      #{{ pendingFaq.source_conversation.display_id }}
                    </span>
                    <Icon
                      icon="i-lucide-external-link"
                      class="size-3.5 flex-shrink-0"
                    />
                  </button>
                </div>

                <!-- 关联助手 -->
                <div v-if="pendingFaq.assistant">
                  <label
                    class="block text-n-slate-12 dark:text-n-slate-12 text-sm font-medium mb-2"
                  >
                    {{ $t('PILOT.PENDING_FAQS.DRAWER.ASSISTANT') }}
                  </label>
                  <p class="text-sm text-n-slate-11">
                    {{ pendingFaq.assistant.name }}
                  </p>
                </div>

                <!-- 创建时间 -->
                <div>
                  <label
                    class="block text-n-slate-12 dark:text-n-slate-12 text-sm font-medium mb-2"
                  >
                    {{ $t('PILOT.PENDING_FAQS.TABLE.CREATED_AT') }}
                  </label>
                  <p class="text-sm text-n-slate-11">
                    {{ dynamicTime(pendingFaq.created_at) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div
          class="flex items-center gap-3 px-6 py-4 border-t border-n-slate-6 dark:border-n-slate-700"
        >
          <template v-if="pendingFaq.status === 'pending'">
            <Button
              :label="$t('PILOT.PENDING_FAQS.DRAWER.APPROVE')"
              icon="i-lucide-check"
              class="flex-1"
              @click="handleApprove"
            />
            <Button
              :label="$t('PILOT.PENDING_FAQS.DRAWER.REJECT')"
              icon="i-lucide-x"
              variant="faded"
              color="ruby"
              class="flex-1"
              @click="handleReject"
            />
            <Button
              :label="$t('PILOT.PENDING_FAQS.DRAWER.DELETE')"
              icon="i-lucide-trash"
              variant="faded"
              color="slate"
              class="flex-1"
              @click="handleDelete"
            />
          </template>
          <Button
            v-else
            variant="faded"
            color="slate"
            :label="$t('PILOT.PENDING_FAQS.DRAWER.CLOSE')"
            class="flex-1"
            @click="closeDrawer"
          />
        </div>
      </div>
    </transition>
  </Teleport>
</template>
